import React from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import ProviderCenter from "./ProviderCenter";
import { useLanguage } from "../contexts/LanguageContext";
import "../styles/provider-dashboard.css";

const sectionLinks = [
  { key: "application", href: "#provider-section-application", icon: "bi-patch-check" },
  { key: "payouts", href: "#provider-section-payouts", icon: "bi-wallet2" },
  { key: "seller", href: "#provider-section-seller", icon: "bi-bag-check" },
  { key: "services", href: "#provider-section-services", icon: "bi-briefcase" },
  { key: "consultations", href: "#provider-section-consultations", icon: "bi-chat-square-dots" },
  { key: "courses", href: "#provider-section-courses", icon: "bi-easel2" },
  { key: "jobs", href: "#provider-section-jobs", icon: "bi-building" },
];

export default function ProviderDashboardPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const isDesktop = useMediaQuery({ minWidth: 1200 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1199 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [activeTab, setActiveTab] = React.useState("overview");
  const [dashboardData, setDashboardData] = React.useState({
    profiles: [],
    content: { courses: [], consultations: [], services: [], jobs: [], products: [] },
    summary: {},
  });
  const [earningsData, setEarningsData] = React.useState({
    summary: { available_balance: 0, total_earned: 0, currency: "USD" },
    requests: [],
  });

  const formatMoney = (amount, currency = "USD") => {
    if (!amount) return `0 ${currency}`;
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
    return formatted;
  };

  const getContentPendingCount = (items) => {
    if (!Array.isArray(items)) return 0;
    return items.filter((item) => item.approvalStatus === "pending" || item.status === "pending").length;
  };

  const sectionStats = {
    application: dashboardData.profiles.length,
    payouts: earningsData.requests?.length || 0,
    seller: dashboardData.content.products?.length || 0,
    services: dashboardData.content.services?.length || 0,
    consultations: dashboardData.content.consultations?.length || 0,
    courses: dashboardData.content.courses?.length || 0,
    jobs: dashboardData.content.jobs?.length || 0,
  };

  const sectionPendingStats = {
    seller: getContentPendingCount(dashboardData.content.products || []),
    services: getContentPendingCount(dashboardData.content.services || []),
    consultations: getContentPendingCount(dashboardData.content.consultations || []),
    courses: getContentPendingCount(dashboardData.content.courses || []),
    jobs: getContentPendingCount(dashboardData.content.jobs || []),
  };

  const handleDataLoad = React.useCallback((data) => {
    if (data.dashboard) {
      setDashboardData(data.dashboard);
    }
    if (data.earnings) {
      setEarningsData(data.earnings);
    }
  }, []);

  const labels = {
    title: isAr ? "لوحة مقدمي الخدمة" : "Provider Dashboard",
    subtitle: isAr
      ? "لوحة مستقلة لإدارة الاعتماد، الأرباح، السحب، والمنتجات والخدمات والاستشارات والكورسات."
      : "A standalone workspace for approvals, earnings, payouts, and managing products, services, consultations, and courses.",
    profile: isAr ? "العودة إلى الحساب" : "Back to profile",
    orders: isAr ? "طلباتي للخدمات" : "My service orders",
    freelancerOrders: isAr ? "طلباتي كمقدم خدمة" : "My freelancer orders",
    consultations: isAr ? "استشاراتي" : "My consultations",
    courses: isAr ? "كورساتي" : "My courses",
    quickNav: isAr ? "التنقل السريع" : "Quick navigation",
    workspace: isAr ? "مساحة العمل" : "Workspace",
    overview: isAr ? "لوحة تشغيل موحدة" : "Unified operating desk",
    guideTitle: isAr ? "كيف تستخدم اللوحة" : "How to use the dashboard",
    guideLineOne: isAr
      ? "ابدأ بحالة الاعتماد، ثم فعّل طرق السحب، وبعدها أدر المحتوى من الأقسام بالأسفل."
      : "Start with approval status, connect payouts, then manage your content from the sections below.",
    guideLineTwo: isAr
      ? "اللوحة مصممة لتجمع كل الأعمال في مكان واحد بدل التنقل بين صفحات متفرقة."
      : "This workspace brings your operating tasks together instead of splitting them across many pages.",
    mobileJump: isAr ? "انتقل بسرعة" : "Jump quickly",
  };

  const sectionTitles = {
    application: isAr ? "الاعتماد" : "Applications",
    payouts: isAr ? "الأرباح والسحب" : "Earnings & payouts",
    seller: isAr ? "منتجات البائع" : "Seller products",
    services: isAr ? "الخدمات" : "Services",
    consultations: isAr ? "الاستشارات" : "Consultations",
    courses: isAr ? "الكورسات" : "Courses",
    jobs: isAr ? "الوظائف" : "Jobs",
  };

  const workspaceLinks = [
    {
      to: "/profile?tab=provider",
      label: labels.profile,
      icon: "bi-person-badge",
      variant: "dark",
    },
    {
      to: "/my-freelancer-orders",
      label: labels.freelancerOrders,
      icon: "bi-kanban",
      variant: "light",
    },
    {
      to: "/my-service-orders",
      label: labels.orders,
      icon: "bi-receipt",
      variant: "light",
    },
    {
      to: "/my-consultations",
      label: labels.consultations,
      icon: "bi-calendar2-week",
      variant: "light",
    },
    {
      to: "/my-courses",
      label: labels.courses,
      icon: "bi-collection-play",
      variant: "light",
    },
  ];

  const sidebarBlocks = (
    <>
      <div className="provider-dashboard-page__panel">
        <div className="provider-dashboard-page__panel-label">{labels.workspace}</div>
        <div
          className={[
            "provider-dashboard-page__nav-grid",
            isMobile ? "provider-dashboard-page__nav-grid--mobile" : "",
            isTablet ? "provider-dashboard-page__nav-grid--tablet" : "",
          ].join(" ")}
        >
          {workspaceLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`provider-dashboard-page__workspace-link provider-dashboard-page__workspace-link--${link.variant}`}
            >
              <span className="provider-dashboard-page__workspace-icon">
                <i className={`bi ${link.icon}`} />
              </span>
              <span>{link.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="provider-dashboard-page__panel provider-dashboard-page__summary-panel">
        <div className="provider-dashboard-page__panel-label">
          {isAr ? "ملخص الأداء" : "Performance Summary"}
        </div>
        <div className="provider-dashboard-page__summary-grid">
          <div className="provider-dashboard-page__summary-item">
            <div className="provider-dashboard-page__summary-label">
              {isAr ? "الرصيد المتاح" : "Available"}
            </div>
            <div className="provider-dashboard-page__summary-value">
              {formatMoney(earningsData.summary?.available_balance || 0, earningsData.summary?.currency || "USD")}
            </div>
          </div>
          <div className="provider-dashboard-page__summary-item">
            <div className="provider-dashboard-page__summary-label">
              {isAr ? "إجمالي الأرباح" : "Total Earned"}
            </div>
            <div className="provider-dashboard-page__summary-value">
              {formatMoney(earningsData.summary?.total_earned || 0, earningsData.summary?.currency || "USD")}
            </div>
          </div>
          <div className="provider-dashboard-page__summary-item">
            <div className="provider-dashboard-page__summary-label">
              {isAr ? "طلبات السحب" : "Withdrawal Requests"}
            </div>
            <div className="provider-dashboard-page__summary-value">
              {earningsData.requests?.length || 0}
            </div>
          </div>
        </div>
      </div>

      <div className="provider-dashboard-page__panel">
        <div className="provider-dashboard-page__panel-label">
          {isMobile ? labels.mobileJump : labels.quickNav}
        </div>
        <div
          className={[
            "provider-dashboard-page__section-list",
            isMobile ? "provider-dashboard-page__section-list--mobile" : "",
            isTablet ? "provider-dashboard-page__section-list--tablet" : "",
          ].join(" ")}
        >
          {sectionLinks.map((section) => {
            const pending = sectionPendingStats[section.key];
            const total = sectionStats[section.key];
            return (
              <button
                key={section.key}
                onClick={() => setActiveTab(section.key)}
                className={`provider-dashboard-page__section-link ${activeTab === section.key ? 'active' : ''}`}
                style={{
                  background: activeTab === section.key ? 'rgba(0,0,0,0.05)' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'inherit',
                  padding: 'inherit',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span className="provider-dashboard-page__section-icon">
                    <i className={`bi ${section.icon}`} />
                  </span>
                  <span>{sectionTitles[section.key]}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {pending > 0 && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px',
                        height: '24px',
                        backgroundColor: '#FF6B6B',
                        color: 'white',
                        borderRadius: '50%',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '0 6px'
                      }}
                    >
                      {pending}
                    </span>
                  )}
                  {total > 0 && !pending && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '24px',
                        height: '24px',
                        backgroundColor: '#6C757D',
                        color: 'white',
                        borderRadius: '50%',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '0 6px'
                      }}
                    >
                      {total}
                    </span>
                  )}
                  <i className={`bi ${isAr ? "bi-arrow-left-short" : "bi-arrow-right-short"}`} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={[
        "provider-dashboard-page",
        isDesktop ? "provider-dashboard-page--desktop" : "",
        isTablet ? "provider-dashboard-page--tablet" : "",
        isMobile ? "provider-dashboard-page--mobile" : "",
      ].join(" ")}
    >
      <div className="provider-dashboard-page__frame">
        <section
          className={[
            "provider-dashboard-page__hero mb-4 mb-xl-5",
            !isDesktop ? "provider-dashboard-page__hero--compact" : "",
          ].join(" ")}
        >
          <div className="provider-dashboard-page__hero-badge">{labels.overview}</div>
          <div className="row g-4" style={{ alignItems: isDesktop ? "flex-start" : "flex-start" }}>
            <div className="col-12 col-xl-7">
              <h1 className="provider-dashboard-page__title">{labels.title}</h1>
              <p className="provider-dashboard-page__subtitle mb-0">{labels.subtitle}</p>
            </div>
            <div className="col-12 col-xl-5">
              <div className="provider-dashboard-page__guide">
                <div className="provider-dashboard-page__guide-title">{labels.guideTitle}</div>
                <p className="mb-2">{labels.guideLineOne}</p>
                <p className="mb-0">{labels.guideLineTwo}</p>
              </div>
            </div>
          </div>
        </section>

        {!isDesktop ? (
          <div className="provider-dashboard-page__stack mb-4">{sidebarBlocks}</div>
        ) : null}

        <div className="row g-4 align-items-start provider-dashboard-page__layout">
          {isDesktop ? (
            <div className="col-12 col-xl-3 provider-dashboard-page__sidebar-col">
              <div className="provider-dashboard-page__sidebar sticky-top" style={{ top: "96px" }}>
                {sidebarBlocks}
              </div>
            </div>
          ) : null}

          <div
            className={[
              isDesktop ? "col-12 col-xl-9" : "col-12",
              "provider-dashboard-page__content-col",
            ].join(" ")}
          >
            <div className="provider-dashboard-page__content-shell">
              <ProviderCenter embedded activeTab={activeTab} setActiveTab={setActiveTab} onDataLoad={handleDataLoad} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
